/*
  Content production page behaviour (sections/cp-intro.liquid, sections/cp-capability.liquid).

  <cp-letters> splits its heading into letters (words stay unbreakable) and builds it letter
  by letter the first time it scrolls into view: each letter turns up from below, out of a
  soft blur. It plays once and stays. Screen readers get the heading's text via aria-label.
  Reduced motion, the theme editor and no JavaScript show the heading as is.

  <cp-marquee> turns its list into an endless horizontal loop: it clones the list until the
  track is at least two screens wide, then moves it by exactly one list width per cycle at
  data-speed px/s. Clones are aria-hidden and inert. It pauses on hover, off screen and in a
  hidden tab; for reduced motion it stands still as a swipeable row.
  One requestAnimationFrame loop does all the work, so the strip, the tile sizes and the gaps
  can never drift apart: it moves the track, and gives every tile --sc (its scale: one tile
  clearly largest at the centre), --x (a shift that keeps the gaps between tiles even as their
  sizes change) and --o (opacity: dimmer away from the centre, fading out at the screen
  edges). No CSS mask on the moving strip: masks over wide, busy layers drop GPU tiles
  (black bands) in Chrome and Safari. No layout reads per frame.
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Marquee tile scale at the screen edges and at the centre.
  const SMIN = 0.74;
  const SMAX = 1.12;
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);

  if (!customElements.get('cp-letters')) {
    class CpLetters extends HTMLElement {
      connectedCallback() {
        this.heading = this.firstElementChild;
        if (!this.heading || this.dataset.split || reduceMotion.matches || designMode() || !('IntersectionObserver' in window)) return;
        this.split();
        this.io = new IntersectionObserver(([e]) => {
          if (!e.isIntersecting) return;
          this.classList.add('is-in');
          this.io.disconnect();
        }, { threshold: 0.35 });
        this.io.observe(this);
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }

      split() {
        const h = this.heading;
        h.setAttribute('aria-label', h.textContent.replace(/\s+/g, ' ').trim());
        let i = 0;
        const walk = (node) => {
          [...node.childNodes].forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE) return walk(child);
            if (child.nodeType !== Node.TEXT_NODE) return;
            const frag = document.createDocumentFragment();
            child.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) return frag.append(' ');
              const word = document.createElement('span');
              word.className = 'cp-letters__word';
              word.setAttribute('aria-hidden', 'true');
              [...part].forEach((ch) => {
                const span = document.createElement('span');
                span.className = 'cp-letters__ch';
                span.style.setProperty('--i', String(i++));
                span.textContent = ch;
                word.append(span);
              });
              frag.append(word);
            });
            child.replaceWith(frag);
          });
        };
        walk(h);
        this.style.setProperty('--n', String(i));
        this.dataset.split = 'true';
        this.classList.add('is-split');
      }
    }
    customElements.define('cp-letters', CpLetters);
  }

  if (!customElements.get('cp-marquee')) {
    class CpMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.list = this.querySelector('[data-list]');
        if (!this.track || !this.list) return;
        this.inView = false;
        this.ac = new AbortController();
        const opts = { signal: this.ac.signal };
        document.addEventListener('visibilitychange', () => this.sync(), opts);
        reduceMotion.addEventListener('change', () => this.build(), opts);
        window.addEventListener('resize', () => this.onResize(), { ...opts, passive: true });
        // Pause on hover (mouse only), like a CSS marquee would.
        this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hover = true; }, opts);
        this.addEventListener('pointerleave', () => { this.hover = false; }, opts);
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(([e]) => {
            this.inView = e.isIntersecting;
            this.sync();
          });
          this.io.observe(this);
        } else {
          this.inView = true;
        }
        this.build();
      }

      disconnectedCallback() {
        if (this.ac) this.ac.abort();
        if (this.io) this.io.disconnect();
        cancelAnimationFrame(this.raf);
        this.stopScale();
      }

      // Scale from a tile's distance d to the screen centre: one clear peak (smoothstep
      // over about 1.5 tiles), SMIN..SMAX.
      scaleAt(d) {
        const v = Math.max(0, 1 - Math.abs(d) / this.reach);
        return SMIN + (SMAX - SMIN) * v * v * (3 - 2 * v);
      }

      // Each frame: advance the strip, scale every tile, then lay the tiles out again so every
      // gap between their scaled edges is the same. The layout is anchored where the screen
      // centre falls between the two tiles around it (at the same fraction of the way across),
      // so it moves smoothly as tiles pass the middle.
      startScale() {
        if (this.scaleRaf || !this.tiles || this.tiles.length < 2) return;
        let last = performance.now();
        const loop = (now) => {
          this.scaleRaf = requestAnimationFrame(loop);
          const dt = Math.min(0.1, (now - last) / 1000);
          last = now;
          if (!this.hover) this.offset = (this.offset + this.speed * dt) % this.listW;
          this.track.style.transform = `translate3d(${(-this.offset).toFixed(2)}px, 0, 0)`;
          const x0 = this.left - this.offset;
          const vw = this.vw;
          const mid = vw / 2;
          const fade = vw * 0.08;
          const { w, gap } = this.geo;
          const T = this.tiles;
          const n = T.length;
          let k = 0;
          for (let i = 0; i < n; i++) {
            T[i].home = x0 + T[i].c;
            T[i].sc = this.scaleAt(T[i].home - mid);
            if (T[i].home <= mid) k = i;
          }
          k = Math.min(k, n - 2);
          const step = (a, b) => (w * (T[a].sc + T[b].sc)) / 2 + gap;
          const f = Math.min(1, Math.max(0, (mid - T[k].home) / (T[k + 1].home - T[k].home)));
          T[k].pos = mid - f * step(k, k + 1);
          for (let i = k + 1; i < n; i++) T[i].pos = T[i - 1].pos + step(i - 1, i);
          for (let i = k - 1; i >= 0; i--) T[i].pos = T[i + 1].pos - step(i, i + 1);
          T.forEach((t) => {
            const x = t.pos - t.home;
            // Edge fade from the tile centre's distance to the nearer screen edge.
            const edge = Math.min(1, Math.max(0, Math.min(t.pos, vw - t.pos) / fade));
            const o = (0.5 + ((t.sc - SMIN) / (SMAX - SMIN)) * 0.5) * edge;
            if (Math.abs(t.sc - t.lastSc) < 0.001 && Math.abs(x - t.lastX) < 0.3 && Math.abs(o - t.lastO) < 0.01) return;
            t.lastSc = t.sc;
            t.lastX = x;
            t.lastO = o;
            t.el.style.setProperty('--sc', t.sc.toFixed(4));
            t.el.style.setProperty('--x', `${x.toFixed(1)}px`);
            t.el.style.setProperty('--o', o.toFixed(3));
          });
        };
        this.scaleRaf = requestAnimationFrame(loop);
      }

      stopScale() {
        cancelAnimationFrame(this.scaleRaf);
        this.scaleRaf = 0;
      }

      onResize() {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.build());
      }

      build() {
        this.track.querySelectorAll('[data-clone]').forEach((c) => c.remove());
        this.classList.remove('is-live');
        this.stopScale();
        this.track.style.removeProperty('transform');
        if (reduceMotion.matches || designMode()) return;
        const listW = this.list.getBoundingClientRect().width;
        if (!listW) return;
        const copies = Math.max(1, Math.ceil((this.clientWidth * 2) / listW) - 1);
        for (let n = 0; n < copies; n++) {
          const clone = this.list.cloneNode(true);
          clone.dataset.clone = '';
          clone.removeAttribute('data-list');
          clone.removeAttribute('aria-label');
          clone.removeAttribute('tabindex');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          this.track.append(clone);
        }
        this.speed = Math.max(5, parseFloat(this.dataset.speed) || 35);
        this.listW = listW;
        this.offset = (this.offset || 0) % listW;
        this.classList.add('is-live');
        this.track.style.transform = 'none';
        this.left = this.getBoundingClientRect().left;
        this.vw = document.documentElement.clientWidth;
        const x0 = this.track.getBoundingClientRect().left;
        this.tiles = [...this.track.querySelectorAll('.cp-marq__item')].map((li) => {
          const r = li.getBoundingClientRect();
          return { el: li.firstElementChild, c: r.left - x0 + r.width / 2, lastSc: -1, lastX: 0, lastO: -1 };
        });
        const w = this.tiles[0] ? this.tiles[0].el.parentElement.getBoundingClientRect().width : 0;
        const pitch = this.tiles[1] ? this.tiles[1].c - this.tiles[0].c : w;
        // Visual gap between scaled tiles = the CSS gap between unscaled ones.
        this.geo = { w, gap: pitch - w };
        this.reach = pitch * 1.5;
        this.sync();
      }

      sync() {
        const paused = !this.inView || document.hidden;
        this.classList.toggle('is-paused', paused);
        if (paused || !this.classList.contains('is-live')) this.stopScale();
        else this.startScale();
      }
    }
    customElements.define('cp-marquee', CpMarquee);
  }
})();
